import { ageLabel } from '../../../utils/records';
import Table from "../../../components/RecordTable";
import useAutoRefresh from "../../../hooks/useAutoRefresh";
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Space,
  Tag,
  Input,
  DatePicker,
  Typography,
  Drawer,
  Descriptions,
  message,
  Row,
  Col,
  Statistic,
  Select,
  Divider,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { careDate } from "../../../utils/records";
import { testBookingAPI } from "../../../services/api";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const statusColor = (s) =>
  ({
    pending: "orange",
    processed: "blue",
    completed: "green",
    cancelled: "red",
    sample_collected: "cyan",
  }[s?.toLowerCase()] || "default");

const SuperAdminTestBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    processed: 0,
    completed: 0,
    cancelled: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [membershipId, setMembershipId] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [hospitalName, setHospitalName] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateRange, setDateRange] = useState(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
      };
      if (filterStatus !== "all") params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      if (membershipId.trim()) params.membershipId = membershipId.trim();
      if (doctorName.trim()) params.doctorName = doctorName.trim();
      if (hospitalName.trim()) params.hospitalName = hospitalName.trim();
      if (dateRange?.[0])
        params.startDate = dateRange[0].startOf("day").toISOString();
      if (dateRange?.[1])
        params.endDate = dateRange[1].endOf("day").toISOString();

      const res = await testBookingAPI.getAll(params);
      const data = Array.isArray(res?.data) ? res.data : res?.data?.bookings || [];
      setBookings(data);
      setPagination((p) => ({ ...p, total: res?.total ?? res?.data?.total ?? data.length }));
      setStatusCounts(
        res?.statusCounts || res?.data?.statusCounts || {
          all: 0,
          pending: 0,
          processed: 0,
          completed: 0,
          cancelled: 0,
        }
      );
    } catch {
      message.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  }, [
    pagination.current,
    pagination.pageSize,
    filterStatus,
    search,
    membershipId,
    doctorName,
    hospitalName,
    dateRange,
  ]);

  useAutoRefresh(() => fetchBookings());

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleApply = () => {
    setPagination((p) => ({ ...p, current: 1 }));
    fetchBookings();
  };

  const handleReset = () => {
    setSearch("");
    setMembershipId("");
    setDoctorName("");
    setHospitalName("");
    setFilterStatus("all");
    setDateRange(null);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const columns = [
    {
      title: "Booking #",
      dataIndex: "bookingNumber",
      key: "bookingNumber",
      width: 130,
      render: (v) => (
        <Text code style={{ fontSize: 12 }}>
          {v || "—"}
        </Text>
      ),
    },
    {
      title: "Patient",
      key: "patient",
      width: 180,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.patient?.name || "—"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.email}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.phone}</div>
        </div>
      ),
    },
    {
      title: "Doctor / Hospital",
      key: "dochosp",
      width: 160,
      render: (_, r) => {
        const u = r["patient.id"] || r.patientUser;
        const dn = u?.doctorName;
        const hn = u?.hospitalName;
        if (!dn && !hn) return <Text type="secondary">—</Text>;
        return (
          <div>
            {dn && <div style={{ fontSize: 12 }}>🩺 {dn}</div>}
            {hn && <div style={{ fontSize: 12 }}>🏥 {hn}</div>}
          </div>
        );
      },
    },
    {
      title: "Membership ID",
      key: "memId",
      width: 140,
      render: (_, r) => {
        const u = r["patient.id"] || r.patientUser;
        const mid = u?.membership?.membershipId;
        return mid ? (
          <Tag color="gold">{mid}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Tests",
      key: "tests",
      width: 180,
      render: (_, r) => (
        <div>
          {r.tests?.slice(0, 2).map((t, i) => (
            <Tag key={i} style={{ marginBottom: 2, fontSize: 11 }}>
              {t.testName}
            </Tag>
          ))}
          {r.tests?.length > 2 && <Tag>+{r.tests.length - 2} more</Tag>}
        </div>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 100,
      render: (_, r) => (
        <div>
          <Text strong>₹{r.totalAmount ?? "—"}</Text>
          {r.discountApplied > 0 && (
            <div style={{ fontSize: 11, color: "#0A6EBD" }}>
              -₹{r.discountApplied} off
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Preferred Date",
      key: "preferredDate",
      width: 130,
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div>{r.preferredDate ? careDate(r.preferredDate) : "—"}</div>
          {r.preferredTimeSlot && (
            <div style={{ color: "#1890ff", fontWeight: 600 }}>{r.preferredTimeSlot}</div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (s) => (
        <Tag color={statusColor(s)}>{s?.toUpperCase() || "—"}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      render: (_, r) => (
        <Button
          size="small"
          type="link"
          onClick={() => {
            setSelected(r);
            setDrawerOpen(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Lab Test Bookings
          </Title>
          <Text type="secondary">
            All lab test bookings across the platform
          </Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchBookings}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Stat Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {[
          {
            label: "Total",
            value: statusCounts.all,
            color: "#1890ff",
            icon: <ExperimentOutlined />,
          },
          {
            label: "Pending",
            value: statusCounts.pending,
            color: "#0A6EBD",
            icon: <ClockCircleOutlined />,
          },
          {
            label: "Processed",
            value: statusCounts.processed,
            color: "#1677ff",
            icon: <CheckCircleOutlined />,
          },
          {
            label: "Completed",
            value: statusCounts.completed,
            color: "#0A6EBD",
            icon: <CheckCircleOutlined />,
          },
          {
            label: "Cancelled",
            value: statusCounts.cancelled,
            color: "#f5222d",
            icon: <CloseCircleOutlined />,
          },
        ].map((s) => (
          <Col xs={12} sm={8} lg={4} key={s.label}>
            <Card size="small">
              <Statistic
                title={s.label}
                value={s.value}
                styles={{ content: { color: s.color, fontSize: 20 } }}
                prefix={s.icon}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Name / Email / Phone / Booking #"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Membership ID"
              value={membershipId}
              onChange={(e) => setMembershipId(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Doctor Name"
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Hospital Name"
              value={hospitalName}
              onChange={(e) => setHospitalName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: "100%" }}
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="processed">Processed</Option>
              <Option value="sample_collected">Sample Collected</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              style={{ width: "100%" }}
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleApply}>
                Search
              </Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={bookings}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} bookings`,
            onChange: (page, pageSize) =>
              setPagination((p) => ({ ...p, current: page, pageSize })),
          }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      {/* Detail Drawer */}
      <Drawer
        title="Booking Details"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
      >
        {selected &&
          (() => {
            const u = selected["patient.id"] || selected.patientUser;
            return (
              <>
                <Tag
                  color={statusColor(selected.status)}
                  style={{ fontSize: 14, marginBottom: 16 }}
                >
                  {selected.status?.toUpperCase()}
                </Tag>

                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions.Item label="Booking #">
                    {selected.bookingNumber || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Patient">
                    {selected.patient?.name || "—"}
                  </Descriptions.Item>
<Descriptions.Item label="Age">{ageLabel(selected?.patient)}</Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selected.patient?.email || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {selected.patient?.phone || "—"}
                  </Descriptions.Item>
                  {u?.membership?.membershipId && (
                    <Descriptions.Item label="Membership ID">
                      <Tag color="gold">{u.membership.membershipId}</Tag>
                    </Descriptions.Item>
                  )}
                  {u?.doctorName && (
                    <Descriptions.Item label="Doctor Name">
                      🩺 {u.doctorName}
                    </Descriptions.Item>
                  )}
                  {u?.hospitalName && (
                    <Descriptions.Item label="Hospital">
                      🏥 {u.hospitalName}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Payment Method">
                    {selected.paymentMethod?.toUpperCase() || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Amount">
                    ₹{selected.totalAmount ?? "—"}
                  </Descriptions.Item>
                  {selected.discountApplied > 0 && (
                    <Descriptions.Item label="Discount">
                      ₹{selected.discountApplied}
                    </Descriptions.Item>
                  )}
                  {selected.preferredDate && (
                    <Descriptions.Item label="Preferred Date">
                      {careDate(selected.preferredDate)}
                    </Descriptions.Item>
                  )}
                  {selected.preferredTimeSlot && (
                    <Descriptions.Item label="Time Slot (IST)">
                      <Tag color="blue">{selected.preferredTimeSlot}</Tag>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Booked On">
                    {careDate(selected.bookingDate || selected.createdAt, true)}
                  </Descriptions.Item>
                </Descriptions>

                {selected.tests?.length > 0 && (
                  <>
                    <Divider orientation="left" style={{ fontSize: 13 }}>
                      Tests ({selected.tests.length})
                    </Divider>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {selected.tests.map((t, i) => (
                        <Card key={i} size="small" style={{ borderRadius: 8 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <Text strong style={{ fontSize: 13 }}>
                                {t.testName}
                              </Text>
                              {t.labName && (
                                <div style={{ fontSize: 12, color: "#888" }}>
                                  {t.labName}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <Text strong>₹{t.price ?? "—"}</Text>
                              {t.originalPrice &&
                                t.originalPrice !== t.price && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "#888",
                                      textDecoration: "line-through",
                                    }}
                                  >
                                    ₹{t.originalPrice}
                                  </div>
                                )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </>
                )}

                {selected.homeCollection?.required && (
                  <>
                    <Divider orientation="left" style={{ fontSize: 13 }}>
                      Home Collection
                    </Divider>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="Address">
                        {selected.homeCollection.address || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="City">
                        {selected.homeCollection.city || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Pincode">
                        {selected.homeCollection.pincode || "—"}
                      </Descriptions.Item>
                      {selected.homeCollection.charge > 0 && (
                        <Descriptions.Item label="Collection Charge">
                          ₹{selected.homeCollection.charge}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </>
                )}
              </>
            );
          })()}
      </Drawer>
    </div>
  );
};

export default SuperAdminTestBookings;
